/**
 * useVault — Hook for Topics, Notes, and Actions (the Vault).
 *
 * Provides CRUD operations for vault entities, cross-board search,
 * and the escalation flow.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '../store/appStore';
import { queryTopics, queryNotes, queryActions, queryNotesByTopic } from '../api/queries';
import {
  mutateCreateTopic,
  mutateCreateNote,
  mutateUpdateNote,
  mutateCreateAction,
  mutateEscalateAction,
} from '../api/mutations';
import type { Topic, Note, Action, EscalationRequest, EscalationResult } from '../types';

const STALE_TIME = 1000 * 60 * 3;

export function useVault() {
  const queryClient = useQueryClient();
  const { boardIds } = useAppStore();
  const topicsBoardId = boardIds?.topics ?? null;
  const notesBoardId = boardIds?.notes ?? null;
  const actionsBoardId = boardIds?.actions ?? null;

  // ---- Queries ----

  const topicsQuery = useQuery<Topic[], Error>({
    queryKey: ['topics', topicsBoardId],
    queryFn: () => queryTopics(topicsBoardId!),
    enabled: Boolean(topicsBoardId),
    staleTime: STALE_TIME,
  });

  const notesQuery = useQuery<Note[], Error>({
    queryKey: ['notes', notesBoardId],
    queryFn: () => queryNotes(notesBoardId!),
    enabled: Boolean(notesBoardId),
    staleTime: STALE_TIME,
  });

  const actionsQuery = useQuery<Action[], Error>({
    queryKey: ['vault_actions', actionsBoardId],
    queryFn: () => queryActions(actionsBoardId!),
    enabled: Boolean(actionsBoardId),
    staleTime: STALE_TIME,
  });

  // ---- Mutations ----

  const createTopic = useMutation({
    mutationFn: (data: Pick<Topic, 'name' | 'category' | 'description'>) =>
      mutateCreateTopic({ boardId: topicsBoardId!, name: data.name, description: data.description }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['topics'] }),
  });

  const createNote = useMutation({
    mutationFn: (data: Pick<Note, 'name' | 'type' | 'content' | 'tags' | 'topicId'>) =>
      mutateCreateNote({ boardId: notesBoardId!, title: data.name, content: data.content, type: data.type, topicId: data.topicId, tags: data.tags }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notes'] }),
  });

  const updateNote = useMutation({
    mutationFn: ({ itemId, updates }: { itemId: string; updates: Partial<Note> }) =>
      mutateUpdateNote({ boardId: notesBoardId!, itemId, content: updates.content, type: updates.type }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notes'] }),
  });

  const createAction = useMutation({
    mutationFn: (data: Pick<Action, 'name' | 'topicId' | 'priority' | 'dueDate' | 'context'>) =>
      mutateCreateAction({ boardId: actionsBoardId!, title: data.name, priority: data.priority, dueDate: data.dueDate, description: data.context }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vault_actions'] }),
  });

  const escalateAction = useMutation<EscalationResult, Error, EscalationRequest>({
    mutationFn: async (request) => {
      const result = await mutateEscalateAction({ targetBoardId: request.targetBoardId, itemName: request.itemName, sourceItemId: request.actionId });
      return { success: true, escalatedItemId: result.id } as EscalationResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vault_actions'] });
    },
  });

  // ---- Notes by topic helper ----

  function useNotesByTopic(topicId: string | null) {
    return useQuery<Note[], Error>({
      queryKey: ['notes_by_topic', topicId],
      queryFn: () => queryNotesByTopic(notesBoardId!, topicId!),
      enabled: Boolean(notesBoardId && topicId),
      staleTime: STALE_TIME,
    });
  }

  return {
    // Data
    topics: topicsQuery.data ?? [],
    notes: notesQuery.data ?? [],
    actions: actionsQuery.data ?? [],
    // Loading states
    isLoadingTopics: topicsQuery.isLoading,
    isLoadingNotes: notesQuery.isLoading,
    isLoadingActions: actionsQuery.isLoading,
    // Mutations
    createTopic: createTopic.mutate,
    createNote: createNote.mutate,
    updateNote: updateNote.mutate,
    createAction: createAction.mutate,
    escalateAction: escalateAction.mutateAsync,
    isEscalating: escalateAction.isPending,
    // Helpers
    useNotesByTopic,
    // Invalidation
    refetchAll: () => {
      queryClient.invalidateQueries({ queryKey: ['topics'] });
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      queryClient.invalidateQueries({ queryKey: ['vault_actions'] });
    },
  };
}
